import React, { useState, useEffect } from 'react';
import { Trash2, BarChart2, Search, AlertTriangle, Edit3, Check, X } from 'lucide-react';
import { UserListItem, DetailedUserStats, UserGroup } from '../types';
import UserStatsModal from './UserStatsModal';
import api from '../services/api';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [userStats, setUserStats] = useState<DetailedUserStats | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  useEffect(() => {
    fetchUsers();
    fetchUserGroups();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const response = await api.get('/admin/user-groups');
      setUserGroups(response.data);
    } catch (error: any) {
      console.error('Error fetching user groups:', error);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  const handleViewStats = async (user: UserListItem) => {
    try {
      setSelectedUser(user);
      setLoading(true);
      setError(null);
      const response = await api.get(`/users/${user.id}/stats`);
      
      // Debug logging to see what we're getting from the API
      console.log('API Response for user stats:', response.data);
      console.log('Response has courseHistory?', !!response.data.courseHistory);
      console.log('CourseHistory length:', response.data.courseHistory?.length || 0);
      
      setUserStats(response.data);
      setShowStatsModal(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch user statistics');
      console.error('Error fetching user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserGroup = async (userId: number, userGroup: string) => {
    try {
      const response = await api.patch(`/users/${userId}/group`, { userGroup });
      
      // Update the local users state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, user_group: userGroup }
          : user
      ));
      
      setEditingUserId(null);
      setSelectedGroup('');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update user group');
      console.error('Error updating user group:', error);
    }
  };

  const startEditingUser = (user: UserListItem) => {
    setEditingUserId(user.id);
    setSelectedGroup(user.user_group || '');
  };

  const cancelEditingUser = () => {
    setEditingUserId(null);
    setSelectedGroup('');
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 pl-10 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-100 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User Group</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    user.is_admin ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {user.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingUserId === user.id ? (
                    <div className="flex items-center space-x-2">
                      <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        className="bg-gray-600 border border-gray-500 rounded-md text-white text-sm px-2 py-1 focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Select group</option>
                        {userGroups.map((group) => (
                          <option key={group.name} value={group.name}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleUpdateUserGroup(user.id, selectedGroup)}
                        className="text-green-400 hover:text-green-300 transition"
                        title="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={cancelEditingUser}
                        className="text-red-400 hover:text-red-300 transition"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.user_group ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.user_group || 'No group'}
                      </span>
                      <button
                        onClick={() => startEditingUser(user)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="Edit Group"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewStats(user)}
                      className="text-blue-400 hover:text-blue-300 flex items-center px-3 py-1 rounded bg-blue-500/10 hover:bg-blue-500/20 transition-colors"
                      title="View Stats"
                    >
                      <BarChart2 className="h-4 w-4 mr-2" />
                      Stats
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-400 hover:text-red-300 flex items-center px-3 py-1 rounded bg-red-500/10 hover:bg-red-500/20 transition-colors"
                      title="Delete User"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showStatsModal && selectedUser && userStats && (
        <UserStatsModal
          user={selectedUser}
          stats={userStats}
          onClose={() => {
            setShowStatsModal(false);
            setSelectedUser(null);
            setUserStats(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;